import './App.css';
import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, Tab, Button, Menu, MenuItem, CircularProgress, Tooltip, IconButton, Snackbar, Alert } from '@mui/material';
import { LoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from './config';
import Map from './components/Map';
import CourtAvailabilityView from './components/CourtAvailabilityView';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { useLocation, useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { getAnalytics, logEvent } from "firebase/analytics";
import LoginPage from './components/LoginPage';
import 'firebase/compat/auth';
import styled from '@emotion/styled';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import MapIcon from '@mui/icons-material/Map';
import ProfilePage from './components/ProfilePage';
import AdminPage from './components/AdminPage';
import PremiumAnnouncementModal from './components/PremiumAnnouncementModal';
import PersonalizedCourtsView from './components/PersonalizedCourtsView';
import ProductTour from './components/ProductTour';
import BrandMark from './components/brand/BrandMark';
import AppLoadingScreen from './components/brand/AppLoadingScreen';
import EmailActionPage from './components/EmailActionPage';
import { firebaseConfig } from './firebase/config';
import { reportHourlyActivity } from './firebase/activity';
import {
  refreshEmailVerification,
  requiresEmailVerification,
  sendAccountVerificationEmail,
} from './firebase/emailVerification';
import { getEffectiveAccountType, shouldShowPremiumAnnouncement, markPremiumAnnouncementSeen } from './utils/premiumAccess';
import { getLoadedMainBundlePath, getLatestMainBundlePath } from './appUpdate';

// Lazily imported so the development-only regression gallery and its fixtures
// are code-split out of the main production chunk. A static import would be
// bundled even though the route below is unreachable in production.
const StyleguideRoute = React.lazy(() => import('./routes/StyleguideRoute'));

const isDevelopment = process.env.NODE_ENV === 'development';
const isStyleguideEnabled = isDevelopment || process.env.REACT_APP_ENABLE_STYLEGUIDE === 'true';
const APP_UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const PRODUCT_TOUR_STORAGE_KEY = 'courtclic:product-tour:new-ui:v1';

let app;

if (!firebase.apps.length) {
  app = firebase.initializeApp(firebaseConfig);
} else {
  app = firebase.app();
}
let analytics = getAnalytics(app);

if (!isDevelopment) {
  analytics = getAnalytics(app);
}

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const StyledTabs = styled(Tabs)({
  '& .MuiTab-root': {
    color: 'var(--cc-text-soft)',
    opacity: 1,
    fontWeight: 700,
    textTransform: 'none',
    '&.Mui-selected': {
      color: 'var(--cc-accent-dark)'
    }
  },
  '& .MuiTabs-indicator': {
    backgroundColor: 'var(--cc-accent)',
    height: 3,
    borderRadius: '3px 3px 0 0'
  }
});

export const formatRelativeTime = (timestamp, isFrench, compact = false) => {
  if (!timestamp) return '';
  
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) {
    if (compact) return `${diffInSeconds} sec`;
    return isFrench ? 
      `Il y a ${diffInSeconds} seconde${diffInSeconds > 1 ? 's' : ''}` :
      `${diffInSeconds} second${diffInSeconds > 1 ? 's' : ''} ago`;
  }
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    if (compact) return `${diffInMinutes} min`;
    return isFrench ?
      `Il y a ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}` :
      `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  }
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    if (compact) return `${diffInHours} h`;
    return isFrench ?
      `Il y a ${diffInHours} heure${diffInHours > 1 ? 's' : ''}` :
      `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  }
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (compact) return `${diffInDays} ${isFrench ? 'j' : 'd'}`;
  return isFrench ?
    `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}` :
    `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
};

const App = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useQuery();
  const [selectedTab, setSelectedTab] = useState(query.get('tab') || 'courtAvailability');
  const [searchField, setSearchField] = useState(query.get('search') || '');
  const [lastRefreshTime, setLastRefreshTime] = useState('');
  const [refreshTimeDisplay, setRefreshTimeDisplay] = useState('');
  const [compactRefreshTimeDisplay, setCompactRefreshTimeDisplay] = useState('');
  const [isFrench, setIsFrench] = useState(true); // Toggle state for language swap
  const [isLoading, setIsLoading] = useState(true);
  // Keep the walkthrough available on demand without interrupting first-time
  // visitors before they have had a chance to use the app.
  const [showProductTour, setShowProductTour] = useState(false);

  // for re-rendering
  const [courtViewKey, setCourtViewKey] = useState(0);



  const [auth, setAuth] = useState(false); // set to true when user is authenticated
  const [userName, setUserName] = useState(''); // set this to the authenticated user's name
  const [anchorPosition, setAnchorPosition] = useState(null);

  const [authChecked, setAuthChecked] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [verificationBusy, setVerificationBusy] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [verificationResendCooldown, setVerificationResendCooldown] = useState(0);

  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);

  const [effectiveAccountType, setEffectiveAccountType] = useState(null);
  const [showPremiumAnnouncement, setShowPremiumAnnouncement] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const isEmailActionRoute = location.pathname === '/auth/action';

  useEffect(() => {
    if (isDevelopment) return undefined;

    const loadedBundlePath = getLoadedMainBundlePath(document);
    if (!loadedBundlePath) return undefined;

    let isActive = true;

    const checkForAppUpdate = async () => {
      try {
        const response = await fetch(`/asset-manifest.json?update-check=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });

        if (!response.ok) return;

        const manifest = await response.json();
        const latestBundlePath = getLatestMainBundlePath(manifest);

        if (isActive && latestBundlePath && latestBundlePath !== loadedBundlePath) {
          setUpdateAvailable(true);
        }
      } catch (error) {
        // An update check should never interrupt normal app use. The next
        // focus, visibility change, or interval will retry automatically.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForAppUpdate();
      }
    };

    checkForAppUpdate();
    const intervalId = window.setInterval(checkForAppUpdate, APP_UPDATE_CHECK_INTERVAL_MS);
    window.addEventListener('focus', checkForAppUpdate);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActive = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', checkForAppUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const handleAppUpdate = () => {
    window.location.reload();
  };

  const handleProductTourClose = () => {
    try {
      window.localStorage.setItem(PRODUCT_TOUR_STORAGE_KEY, 'complete');
    } catch (error) {
      // The tour can still be dismissed if browser storage is unavailable.
    }
    setShowProductTour(false);
  };

  const handleLanguageToggle = () => {
      logEvent(analytics, 'language_change', {
        isFrench: !isFrench,
      });
      const newLanguagePreference = !isFrench;
      setIsFrench(newLanguagePreference);
      
      const user = firebase.auth().currentUser;
      if (user) {
        const docRef = firebase.firestore().collection('userPreferences').doc(user.uid);
        docRef.set({
          preferredLanguage: newLanguagePreference ? 'FR' : 'EN'
        }, { merge: true });
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' }); // Scroll to top when language is toggled
  };

  useEffect(() => {
      const unsubscribe = firebase.auth().onAuthStateChanged(user => {
          if (user) {
              // Do not treat an authenticated user as free while their actual
              // plan is still loading. Plan-aware actions wait for resolution.
              setEffectiveAccountType(null);
              setAuth(true);
              setUserName(user.displayName || 'User');
              setEmailVerificationRequired(requiresEmailVerification(user));
              
              // Fetch the user's preferred language from Firestore
              const docRef = firebase.firestore().collection('userPreferences').doc(user.uid);
              docRef.get().then((doc) => {
                  if (doc.exists) {
                      setIsFrench(doc.data().preferredLanguage === 'FR');
                  }
                  setIsLoading(false);  // Set loading to false once everything is done
                  setAuthChecked(true);
              });
          } else {
              setEffectiveAccountType('free');
              setAuthChecked(true);
              setAuth(false);
              setUserName('');
              setEmailVerificationRequired(false);
              setIsLoading(false);  // Set loading to false
          }
      });

      return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (verificationResendCooldown <= 0) return undefined;

    const intervalId = window.setInterval(() => {
      setVerificationResendCooldown((seconds) => Math.max(0, seconds - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [verificationResendCooldown]);

  useEffect(() => {
    if (!emailVerificationRequired) return undefined;

    const refreshOnFocus = async () => {
      const user = firebase.auth().currentUser;
      if (!user) return;

      try {
        const refreshedUser = await refreshEmailVerification(user);
        setEmailVerificationRequired(requiresEmailVerification(refreshedUser));
      } catch (error) {
        // The explicit check button remains available if a background refresh
        // fails because the device is temporarily offline.
      }
    };

    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [emailVerificationRequired]);

  const handleResendVerificationEmail = async () => {
    const user = firebase.auth().currentUser;
    if (!user || verificationBusy || verificationResendCooldown > 0) return;

    setVerificationBusy(true);
    setVerificationMessage('');

    try {
      await sendAccountVerificationEmail(user, isFrench);
      setVerificationResendCooldown(60);
      setVerificationMessage(isFrench
        ? 'Courriel de vérification envoyé.'
        : 'Verification email sent.');
      logEvent(analytics, 'verification_email_sent', { source: 'banner' });
    } catch (error) {
      setVerificationMessage(isFrench
        ? 'Impossible d’envoyer le courriel pour le moment. Réessayez plus tard.'
        : 'We could not send the email right now. Please try again later.');
      logEvent(analytics, 'auth_error', {
        error: error.message,
        type: 'verification_email',
      });
    } finally {
      setVerificationBusy(false);
    }
  };

  const handleCheckEmailVerification = async () => {
    const user = firebase.auth().currentUser;
    if (!user || verificationBusy) return;

    setVerificationBusy(true);
    setVerificationMessage('');

    try {
      const refreshedUser = await refreshEmailVerification(user);
      const stillRequired = requiresEmailVerification(refreshedUser);
      setEmailVerificationRequired(stillRequired);
      if (stillRequired) {
        setVerificationMessage(isFrench
          ? 'L’adresse n’est pas encore vérifiée.'
          : 'The address is not verified yet.');
      } else {
        logEvent(analytics, 'email_verification_confirmed');
      }
    } catch (error) {
      setVerificationMessage(isFrench
        ? 'Vérification impossible pour le moment.'
        : 'We could not check verification right now.');
    } finally {
      setVerificationBusy(false);
    }
  };

  useEffect(() => {
    if (!auth) return undefined;

    const reportActivity = () => {
      const user = firebase.auth().currentUser;
      if (user) {
        reportHourlyActivity(user).catch(() => {
          // Activity reporting is best-effort and must never interrupt use.
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') reportActivity();
    };

    reportActivity();
    const intervalId = window.setInterval(reportActivity, 15 * 60 * 1000);
    window.addEventListener('focus', reportActivity);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', reportActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [auth]);


  const handleMenu = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setAnchorPosition({
      left: rect.left + rect.width / 2,
      top: rect.bottom,
    });
  };
  
  const handleClose = () => {
    setAnchorPosition(null);
  };

  const handleLogout = () => {
    firebase.auth().signOut().then(() => {
        // Sign-out successful
        setAuth(false);
        setUserName('');
        setAnchorPosition(null);
        setEffectiveAccountType('free');
        setAutoRefreshEnabled(false);
        setCourtViewKey(prevKey => prevKey + 1);
        navigate('/');
    }).catch(error => {
        // Handle errors here
        console.error("Error signing out: ", error);
    });
  };

  const handlePremiumAnnouncementClose = () => {
    const user = firebase.auth().currentUser;
    if (user) {
      markPremiumAnnouncementSeen(user);
    }
    setShowPremiumAnnouncement(false);
  };

  useEffect(() => {
    const db = firebase.firestore();
    db.collection('lastRefreshTime')
      .doc('time')
      .get()
      .then((doc) => {
        if (doc.exists) {
          setLastRefreshTime(doc.data().time);
        } else {
          console.log("No such document!");
        }
      })
      .catch((error) => {
        console.log("Error getting document:", error);
      });
  }, []);

  useEffect(() => {
    console.log(searchField);
  }, [searchField]);

  useEffect(() => {
    console.log(selectedTab);
  }, [selectedTab]);

  const handleTabChange = (event, newTab) => {
    logEvent(analytics, 'tab_change', {
      tab: newTab,
    });
    setSelectedTab(newTab);

    // Navigate to the corresponding route
    switch (newTab) {
      case 'courtAvailability':
        navigate('/');
        break;
      case 'map':
        navigate('/map');
        break;
      case 'myCourts':
        navigate('/my-courts');
        break;
      case 'profile':
        navigate('/profile');
        break;
      case 'login':
        navigate('/login');
        break;
      case 'admin':
        navigate('/admin');
        break;
      case 'account':
        navigate(auth ? '/profile' : '/login');
        break;
      default:
        navigate('/');
    }
  };

  const handleSearchFieldChange = (event) => {
    logEvent(analytics, 'search_change', {
      searchField: event.target.value,
    })
    setSearchField(event.target.value);
  };

  // Memoize the updateLastRefreshTime function
  const updateLastRefreshTime = useCallback(async () => {
    const db = firebase.firestore();
    try {
      const [timeDoc, frequencyDoc] = await Promise.all([
        db.collection('lastRefreshTime').doc('time').get(),
        db.collection('lastRefreshTime').doc('frequency').get()
      ]);

      if (timeDoc.exists) {
        setLastRefreshTime(timeDoc.data().time);
      }
      // Return the frequency in milliseconds (database stores it in seconds)
      return frequencyDoc.exists ? frequencyDoc.data().frequency * 1000 : 5 * 60 * 1000; // Default to 5 minutes
    } catch (error) {
      console.error("Error fetching refresh data:", error);
      return 5 * 60 * 1000; // Default to 5 minutes on error
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  // Separate effect for initial fetch
  useEffect(() => {
    updateLastRefreshTime();
  }, [updateLastRefreshTime]);

  // Separate effect for display updates
  useEffect(() => {
    if (lastRefreshTime) {
      const updateDisplay = () => {
        setRefreshTimeDisplay(formatRelativeTime(lastRefreshTime, isFrench));
        setCompactRefreshTimeDisplay(formatRelativeTime(lastRefreshTime, isFrench, true));
      };
      
      updateDisplay(); // Initial update
      const timer = setInterval(updateDisplay, 10000);

      return () => clearInterval(timer);
    }
  }, [lastRefreshTime, isFrench]);

  useEffect(() => {
    if (!auth) {
      setEffectiveAccountType('free');
      setAutoRefreshEnabled(false);
      return undefined;
    }

    const user = firebase.auth().currentUser;
    if (!user) return undefined;

    setEffectiveAccountType(null);
    const unsubscribe = firebase.firestore().collection('users').doc(user.uid).onSnapshot((doc) => {
      const userType = doc.exists ? (doc.data().accountType || 'free') : 'free';
      const effective = getEffectiveAccountType(userType);
      setEffectiveAccountType(effective);

      if (shouldShowPremiumAnnouncement(user) && userType === 'free') {
        setShowPremiumAnnouncement(true);
      }

      if (effective === 'free') {
        setAutoRefreshEnabled(false);
        firebase.firestore().collection('userPreferences').doc(user.uid).set({
          autoRefreshEnabled: false
        }, { merge: true });
      } else {
        firebase.firestore().collection('userPreferences').doc(user.uid).get().then((prefDoc) => {
          if (prefDoc.exists) {
            setAutoRefreshEnabled(prefDoc.data().autoRefreshEnabled || false);
          }
        });
      }
    }, (error) => {
      console.error('Error loading account type:', error);
      // Keep the account unresolved. Unknown status must not unlock Premium
      // actions or trigger free-plan enforcement.
      setAutoRefreshEnabled(false);
    });

    return () => unsubscribe();
  }, [auth]);

  // Get current route for tab value
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path === '/') return 'courtAvailability';
    if (path === '/map') return 'map';
    if (path === '/my-courts') return 'myCourts';
    if (path === '/profile') return 'profile';
    if (path === '/login') return 'login';
    if (path === '/admin') return 'admin';
    return 'courtAvailability';
  };
  
  if (isEmailActionRoute && !isLoading) {
    return <EmailActionPage fallbackIsFrench={isFrench} />;
  }

  return (
    <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
      <div className="main app-shell">
        {isLoading ? (
          <AppLoadingScreen isFrench={isFrench} />
        ) : (
          <>
            <header className="app-header">
              <button className="app-brand" onClick={() => navigate('/')} aria-label="CourtClic home">
                <BrandMark className="brand-mark" />
                <span>CourtClic</span>
              </button>

              <StyledTabs
                className="app-primary-nav"
                value={['courtAvailability', 'myCourts', 'map'].includes(getCurrentTab()) ? getCurrentTab() : false}
                onChange={handleTabChange}
                aria-label={isFrench ? 'Navigation principale' : 'Primary navigation'}
              >
                <Tab label={isFrench ? 'Recherche' : 'Search'} value="courtAvailability" />
                <Tab label={isFrench ? 'Mes terrains' : 'My Courts'} value="myCourts" />
                <Tab label={isFrench ? 'Carte' : 'Map'} value="map" />
                <Tab sx={{ display: 'none' }} value="profile" />
                <Tab sx={{ display: 'none' }} value="login" />
                <Tab sx={{ display: 'none' }} value="admin" />
              </StyledTabs>

              <div className="app-header-actions">
                <div className="refresh-status">
                  <span className={`refresh-dot ${autoRefreshEnabled ? 'is-live' : ''}`} />
                  <span className="refresh-time refresh-time--full">
                    {refreshTimeDisplay || (isFrench ? 'À jour' : 'Live')}
                  </span>
                  <span className="refresh-time refresh-time--compact">
                    {compactRefreshTimeDisplay || (isFrench ? 'À jour' : 'Live')}
                  </span>
                </div>

                <div className="language-toggle" aria-label={isFrench ? 'Langue' : 'Language'}>
                  <button className={!isFrench ? 'is-active' : ''} onClick={isFrench ? handleLanguageToggle : undefined}>EN</button>
                  <button className={isFrench ? 'is-active' : ''} onClick={!isFrench ? handleLanguageToggle : undefined}>FR</button>
                </div>

                {authChecked ? (
                  auth ? (
                    <>
                      <Tooltip title={isFrench ? `Salut, ${userName.split(' ')[0]}!` : `Hi, ${userName.split(' ')[0]}!`}>
                        <IconButton
                          aria-controls="menu-appbar"
                          aria-haspopup="true"
                          onClick={handleMenu}
                          className="account-avatar"
                        >
                          <span>{userName.split(' ').map(part => part[0]).join('').slice(0, 2).toUpperCase() || <AccountCircleIcon />}</span>
                        </IconButton>
                      </Tooltip>
                      <Menu
                        id="menu-appbar"
                        anchorPosition={anchorPosition}
                        anchorReference="anchorPosition"
                        keepMounted
                        open={Boolean(anchorPosition)}
                        onClose={handleClose}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                      >
                        <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
                          {isFrench ? 'Mon profil' : 'My profile'}
                        </MenuItem>
                        <MenuItem onClick={handleLogout}>{isFrench ? 'Déconnexion' : 'Sign out'}</MenuItem>
                      </Menu>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate('/login')}
                      startIcon={<PersonAddAltIcon />}
                      className="sign-in-button"
                    >
                      {isFrench ? 'Connexion' : 'Sign in'}
                    </Button>
                  )
                ) : (
                  <CircularProgress size={22} />
                )}
              </div>
            </header>

            {emailVerificationRequired && (
              <section className="email-verification-banner" role="status" aria-live="polite">
                <div className="email-verification-copy">
                  <strong>
                    {isFrench ? 'Votre adresse courriel n’est pas vérifiée.' : 'Your email address is not verified.'}
                  </strong>
                  <span>
                    {isFrench
                      ? 'Vous pouvez continuer à utiliser CourtClic, mais vérifiez-la pour conserver votre compte. Les comptes non vérifiés peuvent être supprimés.'
                      : 'You can keep using CourtClic, but verify it to keep your account. Unverified accounts may be removed.'}
                  </span>
                  {verificationMessage && <small>{verificationMessage}</small>}
                </div>
                <div className="email-verification-actions">
                  <Button
                    size="small"
                    variant="text"
                    disabled={verificationBusy || verificationResendCooldown > 0}
                    onClick={handleResendVerificationEmail}
                  >
                    {verificationResendCooldown > 0
                      ? `${isFrench ? 'Renvoyer' : 'Resend'} (${verificationResendCooldown}s)`
                      : (isFrench ? 'Renvoyer le courriel' : 'Resend email')}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={verificationBusy}
                    onClick={handleCheckEmailVerification}
                  >
                    {verificationBusy
                      ? <CircularProgress size={16} color="inherit" />
                      : (isFrench ? 'J’ai vérifié' : 'I verified')}
                  </Button>
                </div>
              </section>
            )}

            <main className={getCurrentTab() === 'map' ? 'app-content app-content--map' : 'app-content'}>
              <Routes>
                <Route path="/" element={
                  <CourtAvailabilityView
                    key={courtViewKey}
                    searchField={searchField}
                    onSearchFieldChange={handleSearchFieldChange}
                    isFrench={isFrench}
                    analytics={analytics}
                    setSelectedTab={handleTabChange}
                    updateLastRefreshTime={updateLastRefreshTime}
                    autoRefreshEnabled={autoRefreshEnabled}
                    auth={auth}
                    accountType={effectiveAccountType}
                  />
                } />
                <Route path="/map" element={
                  <Map
                    isFrench={isFrench}
                    analytics={analytics}
                    updateLastRefreshTime={updateLastRefreshTime}
                    autoRefreshEnabled={autoRefreshEnabled}
                    auth={auth}
                    accountType={effectiveAccountType}
                  />
                } />
                <Route path="/my-courts" element={
                  <PersonalizedCourtsView
                    isFrench={isFrench}
                    analytics={analytics}
                    auth={auth}
                    accountType={effectiveAccountType}
                    updateLastRefreshTime={updateLastRefreshTime}
                  />
                } />
                <Route path="/profile" element={
                  auth ? (
                    <ProfilePage
                      isFrench={isFrench}
                      analytics={analytics}
                      autoRefreshEnabled={autoRefreshEnabled}
                      setAutoRefreshEnabled={setAutoRefreshEnabled}
                      effectiveAccountType={effectiveAccountType}
                    />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                } />
                <Route path="/login" element={
                  <LoginPage
                    isFrench={isFrench}
                    analytics={analytics}
                    setSelectedTab={handleTabChange}
                  />
                } />
                <Route path="/admin" element={
                  auth ? <AdminPage isFrench={isFrench} /> : <Navigate to="/login" replace />
                } />
                {isStyleguideEnabled && (
                  <Route path="/styleguide" element={
                    <React.Suspense fallback={<CircularProgress size={26} />}>
                      <StyleguideRoute analytics={analytics} />
                    </React.Suspense>
                  } />
                )}
              </Routes>
              {getCurrentTab() === 'map' && (
                <a
                  className="map-donation-link"
                  href="https://www.buymeacoffee.com/zilongl"
                  onClick={() => logEvent(analytics, 'donation_link_click', { placement: 'map' })}
                  target="_blank"
                  rel="noreferrer"
                >
                  {isFrench ? 'Soutenir CourtClic ♥' : 'Support CourtClic ♥'}
                </a>
              )}
            </main>

            <nav className="mobile-bottom-nav" aria-label={isFrench ? 'Navigation mobile' : 'Mobile navigation'}>
              <button className={getCurrentTab() === 'courtAvailability' ? 'is-active' : ''} onClick={() => navigate('/')}>
                <SearchIcon fontSize="small" />
                <span>{isFrench ? 'Recherche' : 'Search'}</span>
              </button>
              <button className={getCurrentTab() === 'myCourts' ? 'is-active' : ''} onClick={() => navigate('/my-courts')}>
                <StarIcon fontSize="small" />
                <span>{isFrench ? 'Terrains' : 'Courts'}</span>
              </button>
              <button className={getCurrentTab() === 'map' ? 'is-active' : ''} onClick={() => navigate('/map')}>
                <MapIcon fontSize="small" />
                <span>{isFrench ? 'Carte' : 'Map'}</span>
              </button>
              <button className={['profile', 'login'].includes(getCurrentTab()) ? 'is-active' : ''} onClick={() => navigate(auth ? '/profile' : '/login')}>
                {auth ? <AccountCircleIcon fontSize="small" /> : <PersonAddAltIcon fontSize="small" />}
                <span>{auth ? (isFrench ? 'Profil' : 'Profile') : (isFrench ? 'Connexion' : 'Sign in')}</span>
              </button>
            </nav>

            <footer className={getCurrentTab() === 'map' ? 'app-footer app-footer--map' : 'app-footer'}>
              <div className="app-footer-primary">
                <span>{isFrench ? 'Terrains municipaux de Montréal · disponibilités en direct' : 'Montréal municipal courts · live availability'}</span>
                <a
                  href="https://www.buymeacoffee.com/zilongl"
                  onClick={() => logEvent(analytics, 'donation_link_click')}
                  target="_blank"
                  rel="noreferrer"
                >
                  {isFrench ? 'Fait avec ♥ et café à Montréal' : 'Made with ♥ and coffee in Montréal'}
                </a>
              </div>
              {['courtAvailability', 'myCourts', 'profile'].includes(getCurrentTab()) && (
                <div className="mobile-page-language">
                  <span className="mobile-page-language-label">
                    {isFrench ? 'Langue' : 'Language'}
                  </span>
                  <div className="language-toggle" aria-label={isFrench ? 'Langue' : 'Language'}>
                    <button
                      className={!isFrench ? 'is-active' : ''}
                      onClick={isFrench ? handleLanguageToggle : undefined}
                      aria-label="English"
                    >
                      EN
                    </button>
                    <button
                      className={isFrench ? 'is-active' : ''}
                      onClick={!isFrench ? handleLanguageToggle : undefined}
                      aria-label="Français"
                    >
                      FR
                    </button>
                  </div>
                </div>
              )}
              <button className="app-footer-tour" type="button" onClick={() => setShowProductTour(true)}>
                {isFrench ? 'Voir le guide du nouveau CourtClic' : 'View the new CourtClic tour'}
              </button>
              <div className="app-footer-legacy">
                <span>{isFrench ? 'Vous préférez l’ancienne interface?' : 'Prefer the previous interface?'}</span>
                <a href="https://legacy.courtclic.com">
                  {isFrench ? 'Ouvrir CourtClic classique' : 'Open classic CourtClic'}
                </a>
              </div>
            </footer>

          </>
        )}

        <PremiumAnnouncementModal
          open={showPremiumAnnouncement}
          onClose={handlePremiumAnnouncementClose}
          isFrench={isFrench}
        />
        <ProductTour
          open={showProductTour && !isLoading}
          isFrench={isFrench}
          onLanguageChange={handleLanguageToggle}
          onClose={handleProductTourClose}
        />
        <Snackbar
          className="app-update-snackbar"
          open={updateAvailable}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            className="app-update-alert"
            icon={<AutorenewIcon fontSize="inherit" />}
            action={
              <Button color="inherit" size="small" onClick={handleAppUpdate}>
                {isFrench ? 'Mettre à jour' : 'Update'}
              </Button>
            }
          >
            {isFrench
              ? 'Une nouvelle version de CourtClic est disponible.'
              : 'A new version of CourtClic is available.'}
          </Alert>
        </Snackbar>
      </div>
    </LoadScript>
  );
};

export default App;
